import IconButtonDSC from '@deltek/specification-client/core/Buttons/IconButton'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { debounce } from '@deltek/specification-client/core/Utilities/formUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { SvgIcon, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import Input from '@material-ui/core/Input'
import InputAdornment from '@material-ui/core/InputAdornment'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { default as FilterListIcon } from '@material-ui/icons/FilterList'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import ListIcon from '@material-ui/icons/List'
import PrintIcon from '@material-ui/icons/Print'
import RefreshIcon from '@material-ui/icons/Refresh'
import SearchIcon from '@material-ui/icons/Search'
import ViewModuleIcon from '@material-ui/icons/ViewModule'
import clsx from 'clsx'
import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import BulkActionsButton from './BulkActionsButton'
import { sharedTableStyles } from './Spectable.styles'
import ExportIcon from './assets/ExportIcon'
import { TableSettings, TableSettingsProps } from './settings/TableSettings'
import { TableSettingsState } from './settings/store'

const tableTitleSettingsStyles = makeStyles((theme: Theme) => ({
    expandCollapseAllContainer: {
        '& button': {
            borderRadius: theme.spacing(2.5),
            marginRight: theme.spacing(0.5),
        },
        '& button:first-child': {
            marginRight: theme.spacing(1),
        },
    },
    tableTitleSettingsContainer: {
        minHeight: 50,
        '&.dark': {
            backgroundColor: '#343334',
            color: '#fff',
            '& button': {
                ...theme.typography.body2,
                color: 'inherit',
            },
            '& .MuiDivider-vertical': {
                backgroundColor: '#fff',
            },
        },
        '&.light': {
            color: '#4F4F4F',
            '& button': {
                ...theme.typography.body2,
                color: 'inherit',
            },
            '& .MuiDivider-vertical': {
                backgroundColor: '#4F4F4F',
            },
            '& .tableSearchInput': {
                border: '1px solid #4F4F4F',
            },
            '& svg': {
                borderColor: '#4F4F4F',
            },
        },
    },
    tableSettingsPadding: {
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1),
    },
    tableSettingsFlexCenter: {
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
    },
    viewAsContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    iconBorder: {
        border: '1px solid',
        borderRadius: 2,
    },
    activeIconBackgroundColor: {
        backgroundColor: '#13B5EA',
        color: '#fff',
    },
    refreshIcon: {
        color: '#314959',
    },
    displayFlex: {
        display: 'flex',
    },
    displayNone: {
        display: 'none',
    },
    searchInput: {
        fontSize: pxToRem(14),
        backgroundColor: '#fff',
        borderRadius: theme.spacing(1),
        '& input': {
            marginLeft: theme.spacing(1),
        },
        '&.MuiInput-underline:after,&.MuiInput-underline:before,&.MuiInput-underline:hover:not(.Mui-disabled):before': {
            transition: 'unset',
            borderBottom: 'unset',
            transform: 'unset',
        },
        '& .MuiInputAdornment-positionEnd': {
            marginRight: theme.spacing(1),
        },
    },
    verticalDivider: {
        height: '70%',
        marginTop: theme.spacing(1),
    },
    lastUpdatedContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    lastUpdated: {
        color: '#153D60',
        paddingLeft: pxToRem(12),
        paddingRight: pxToRem(12),
    },
    refreshButtonContainer: {
        paddingLeft: pxToRem(12),
        paddingRight: pxToRem(12),
        display: 'flex',
        alignItems: 'center',
        '&  button': {
            padding: 0,
        },
        '& svg': {
            height: pxToRem(24),
            width: pxToRem(24),
            color: '#153D60',
        },
    },
}))

export interface SpecTableTitleSettingsProps<TData extends Object> {
    /**
     * This hides the collapse all / expand all entirely if there is no grouping. Default false
     */
    hasGrouping?: boolean
    /**
     * Enable/Disable bulk actions
     */
    isBulkActionsEnabled?: boolean
    /**
     * The initial global search value.
     */
    initialGlobalSearchValue?: string
    /**
     * The event that gets called when a user clicks bulk action.
     */
    onBulkActionsClick?: (action: string) => void
    /**
     * The event that gets called when a user toggles to card view.  This will hide the whole table.
     * If onCardViewClick and onTableViewClick is undefined,
     * it will hide the whole View As option on the table title settings row.
     */
    onCardViewClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    /**
     * The event that gets called when collapse all gets clicked.  If undefined, it will hide the
     * Expand All / Collapse All buttons. It won't show if hasGrouping is false.
     */
    onCollapseAllClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    /**
     * The event that gets called when expand all gets clicked.  If undefined, it will hide the
     * Expand All / Collapse All buttons. It won't show if hasGrouping is false.
     */
    onExpandAllClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    /**
     * The event that gets called when the filter button gets clicked.  If undefined, filter button will not show.
     */
    onFilterClick?: (showFilters: boolean) => void
    /**

     * The event that gets called when the refresh button gets clicked.  If undefined, refresh button will not show.
     * Show or hide the LastUpdate text.
     * Default: undefined
     */
    onRefreshClick?: () => void
    /**
     * Show or hide the LastUpdate text.
     * Default: undefined
     */
    lastUpdate?: string
    /**
     * The event that gets called when the export button gets clicked.  If undefined, export button will not show.
     */
    onReportsExportClick?: () => void
    /**
     * show export loading
     */
    isExportLoading?: boolean
    /**
     * The event that gets called when Clear all filter clicked.
     * If undefined, it will hide the Clear All filter button.
     */
    onClearAllFilterClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    /**
     * The global search value changes.  If this is undefined, global search will be hidden from the UI.
     * This is debounced by 300ms similar to filters.
     */
    onGlobalSearchValueChange?: (searchValue: string) => void
    /**
     * On print event handler.  Hides the printer when onPrintClick is undefined.
     */
    onPrintClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    /**
     * On export event handler.  Hides the export when onExportClick is undefined.
     */
    onExportClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    /**
     * The event that gets called when a user toggles to table view.
     * If onCardViewClick and onTableViewClick is undefined,
     * it will hide the whole View As option on the table title settings row.
     */
    onTableViewClick?: (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => void
    /**
     * Function to update the table view
     */
    saveTableViewSettings?: (state: TableSettingsState) => void
    /**
     * Show or hide the filters row.  Not to confuse with showFilterOption which show or hides the option to toggle this option.
     * This is the default value of the filter option component. Default: true
     */
    showFiltersRow?: boolean
    /**
     * Show or hide the Bulk Actions Button
     * Default: false
     */
    showBulkActionsButton?: boolean
    /**
     * Show or hide the Clear All button.
     * Default: false
     */
    showClearAllFilterButton?: boolean
    /**
     * Show or hide the table settings icon
     * If true and the table settings is undefined, it won't show the settings icon
     * Default: true
     */
    showTableSettings?: boolean
    /**
     * Settings configuration. If undefined, hides the cog/settings button.
     */
    tableSettings?: TableSettingsProps<TData>
    /**
     * Table title
     */
    tableTitle?: JSX.Element | string
    /**
     * Sets the table view icon to active. Default true
     */
    tableViewActive?: boolean
    /**
     * Default is dark
     */
    theme?: 'dark' | 'light'
    /**
     * The width of the bar.  If not passed, it will use the width of the table if there is any
     */
    width?: string
}

const SpecTableTitleSettings = <TData extends Object>({
    hasGrouping = false,
    isBulkActionsEnabled = false,
    initialGlobalSearchValue,
    onBulkActionsClick,
    onCardViewClick,
    onCollapseAllClick,
    onExpandAllClick,
    onFilterClick,
    onRefreshClick,
    lastUpdate,
    onClearAllFilterClick,
    onGlobalSearchValueChange,
    onPrintClick,
    onExportClick,
    onReportsExportClick,
    isExportLoading,
    onTableViewClick,
    saveTableViewSettings,
    showBulkActionsButton = false,
    showFiltersRow = true,
    showClearAllFilterButton = false,
    showTableSettings = true,
    tableTitle,
    theme = 'dark',
    tableSettings,
    tableViewActive = true,
    width,
}: SpecTableTitleSettingsProps<TData>) => {
    const classes = tableTitleSettingsStyles()
    const { cellPadding } = sharedTableStyles()
    const searchInputRef = useRef<HTMLInputElement>()

    const [searchFocusedIn, setSearchFocusedIn] = useState<boolean>(false)

    const debouncedSearchValueChange = useCallback(
        debounce(onGlobalSearchValueChange!),
        []
    )

    const handleFilterClick = (
        _e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        onFilterClick?.(!showFiltersRow)
    }
    const handleRefreshClick = (
        _e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        onRefreshClick?.()
    }
    const handleReportsExportClick = (
        _e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        onReportsExportClick?.()
    }
    const handleGlobalSearchValueChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        if (onGlobalSearchValueChange)
            debouncedSearchValueChange(event.currentTarget.value)
    }

    const handleSearchBlur = (
        event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        if (!event.currentTarget.value) setSearchFocusedIn(false)
    }

    const handleSearchButtonClick = (
        _e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        setSearchFocusedIn(true)
    }

    useEffect(() => {
        if (searchFocusedIn) searchInputRef.current?.focus()
    }, [searchFocusedIn])

    return (
        <GridContainer
            alignItems="center"
            className={clsx(
                'table-title-settings-container',
                classes.tableTitleSettingsContainer,
                cellPadding,
                theme
            )}
            style={{ width }}
        >
            <GridItem xs={4} container justifyContent="flex-start">
                <GridItem className="table-title-container">
                    {tableTitle}
                </GridItem>
            </GridItem>
            <GridItem
                xs={8}
                container
                justifyContent="flex-end"
                className="table-options"
            >
                {!!onGlobalSearchValueChange && (
                    <GridItem
                        className={clsx(
                            classes.tableSettingsPadding,
                            classes.tableSettingsFlexCenter,
                            'global-search-container'
                        )}
                    >
                        <Button
                            onClick={handleSearchButtonClick}
                            className={clsx(
                                'global-search-button',
                                searchFocusedIn
                                    ? classes.displayNone
                                    : classes.displayFlex
                            )}
                        >
                            Search &nbsp;
                            <SearchIcon fontSize="small" />
                        </Button>
                        <Input
                            style={{ backgroundColor: 'white' }}
                            placeholder="Search"
                            inputRef={searchInputRef}
                            onChange={handleGlobalSearchValueChange}
                            onBlur={handleSearchBlur}
                            defaultValue={initialGlobalSearchValue}
                            className={clsx(
                                'tableSearchInput',
                                classes.searchInput,
                                searchFocusedIn
                                    ? classes.displayFlex
                                    : classes.displayNone
                            )}
                            endAdornment={
                                <InputAdornment position="end">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            }
                        />
                    </GridItem>
                )}
                {tableViewActive && !!showBulkActionsButton && (
                    <>
                        <GridItem className="export-option-divider">
                            <Divider
                                orientation="vertical"
                                flexItem
                                className={classes.verticalDivider}
                            />
                        </GridItem>
                        <GridItem
                            className={clsx(
                                classes.tableSettingsFlexCenter,
                                'export-option-container'
                            )}
                        >
                            <BulkActionsButton
                                onBulkActionsClick={onBulkActionsClick}
                                isBulkActionsEnabled={isBulkActionsEnabled}
                            />
                        </GridItem>
                    </>
                )}
                {(!!onExportClick || !!onPrintClick) && (
                    <GridItem className="export-option-divider">
                        <Divider
                            orientation="vertical"
                            flexItem
                            className={classes.verticalDivider}
                        />
                    </GridItem>
                )}
                {tableViewActive && !!onExportClick && (
                    <>
                        <GridItem
                            className={clsx(
                                classes.tableSettingsFlexCenter,
                                'export-option-container'
                            )}
                        >
                            <Button
                                onClick={onExportClick}
                                data-testid="btnExport"
                            >
                                Export &nbsp; <ExportIcon />
                            </Button>
                        </GridItem>
                    </>
                )}
                {!!onPrintClick && (
                    <>
                        <GridItem
                            className={clsx(
                                classes.tableSettingsFlexCenter,
                                'print-option-container'
                            )}
                        >
                            <Button onClick={onPrintClick}>
                                Print &nbsp;{' '}
                                <PrintIcon
                                    fontSize="small"
                                    className={classes.iconBorder}
                                />
                            </Button>
                        </GridItem>
                    </>
                )}
                {!!lastUpdate && (
                    <>
                        <GridItem
                            className={clsx(
                                classes.lastUpdatedContainer,
                                'last-update-container'
                            )}
                        >
                            <Typography className={classes.lastUpdated}>
                                Last Updated: {lastUpdate}
                            </Typography>
                        </GridItem>
                    </>
                )}
                {!!onRefreshClick && (
                    <>
                        <GridItem className={classes.refreshButtonContainer}>
                            <IconButton
                                onClick={handleRefreshClick}
                                data-testid="btnRefreshTableData"
                                aria-label="Refresh"
                            >
                                <RefreshIcon
                                    fontSize="small"
                                    className={classes.refreshIcon}
                                />
                            </IconButton>
                        </GridItem>
                    </>
                )}
                {!!onReportsExportClick && (
                    <>
                        <GridItem className={classes.refreshButtonContainer}>
                            <IconButtonDSC
                                onClick={handleReportsExportClick}
                                data-testid="btnExportTableData"
                                loading={isExportLoading}
                                backgroundColor="#ffffff"
                                hasShadow={false}
                                aria-label="Export"
                            >
                                <SvgIcon viewBox="-2 -2 28 28" fill="#314959">
                                    <g mask="url(#mask0_27_26)">
                                        <path d="M6 23C5.45 23 4.97917 22.8042 4.5875 22.4125C4.19583 22.0208 4 21.55 4 21V10C4 9.45 4.19583 8.97917 4.5875 8.5875C4.97917 8.19583 5.45 8 6 8H9V10H6V21H18V10H15V8H18C18.55 8 19.0208 8.19583 19.4125 8.5875C19.8042 8.97917 20 9.45 20 10V21C20 21.55 19.8042 22.0208 19.4125 22.4125C19.0208 22.8042 18.55 23 18 23H6ZM11 16V4.825L9.4 6.425L8 5L12 1L16 5L14.6 6.425L13 4.825V16H11Z" />
                                    </g>
                                </SvgIcon>
                            </IconButtonDSC>
                        </GridItem>
                    </>
                )}
                {hasGrouping && (
                    <>
                        <GridItem className="expand-collapse-all-divider">
                            <Divider
                                orientation="vertical"
                                flexItem
                                className={classes.verticalDivider}
                            />
                        </GridItem>
                        <GridItem
                            className={clsx(
                                classes.tableSettingsPadding,
                                classes.tableSettingsFlexCenter,
                                classes.expandCollapseAllContainer,
                                'expand-collapse-all-container'
                            )}
                        >
                            {!!onExpandAllClick && (
                                <Button
                                    variant="outlined"
                                    endIcon={<KeyboardArrowDownIcon />}
                                    className={clsx(
                                        classes.iconBorder,
                                        'expand-all-button'
                                    )}
                                    onClick={onExpandAllClick}
                                >
                                    Expand All
                                </Button>
                            )}
                            {!!onCollapseAllClick && (
                                <Button
                                    variant="outlined"
                                    endIcon={<KeyboardArrowUpIcon />}
                                    className={clsx(
                                        classes.iconBorder,
                                        'collapse-all-button'
                                    )}
                                    onClick={onCollapseAllClick}
                                >
                                    Collapse All
                                </Button>
                            )}
                        </GridItem>
                    </>
                )}
                {(!!onCardViewClick || !!onTableViewClick) && (
                    <>
                        <GridItem className="view-as-option-divider">
                            <Divider
                                orientation="vertical"
                                flexItem
                                className={classes.verticalDivider}
                            />
                        </GridItem>
                        <GridItem
                            className={clsx(
                                classes.viewAsContainer,
                                classes.tableSettingsPadding,
                                'view-as-option-container'
                            )}
                        >
                            <span>View as:</span>
                            <Button
                                onClick={onCardViewClick}
                                className="view-as-option-card-button"
                                data-testid="btnViewAsCard"
                            >
                                <ViewModuleIcon
                                    fontSize="small"
                                    className={clsx(
                                        !tableViewActive &&
                                            classes.activeIconBackgroundColor,
                                        'view-as-card',
                                        !tableViewActive && 'active'
                                    )}
                                />
                                &nbsp; Card
                            </Button>
                            <Button
                                onClick={onTableViewClick}
                                className="view-as-option-table-button"
                                data-testid="btnViewAsTable"
                            >
                                <ListIcon
                                    fontSize="small"
                                    className={clsx(
                                        tableViewActive &&
                                            classes.activeIconBackgroundColor,
                                        'view-as-table',
                                        tableViewActive && 'active'
                                    )}
                                />
                                &nbsp; Table
                            </Button>
                        </GridItem>
                    </>
                )}
                {!!showClearAllFilterButton && (
                    <>
                        <GridItem className="clear-filter-divider">
                            <Divider
                                orientation="vertical"
                                flexItem
                                className={classes.verticalDivider}
                            />
                        </GridItem>
                        <GridItem className="clear-filter-container">
                            <Button
                                onClick={onClearAllFilterClick}
                                className="clear-filter-button"
                            >
                                Clear Filter
                            </Button>
                        </GridItem>
                    </>
                )}
                {!!onFilterClick && !!tableViewActive && (
                    <>
                        <GridItem className="filter-option-divider">
                            <Divider
                                orientation="vertical"
                                flexItem
                                className={classes.verticalDivider}
                            />
                        </GridItem>
                        <GridItem className="filter-option-container">
                            <IconButton
                                onClick={handleFilterClick}
                                className="filter-option-button"
                                data-testid="btnDisplayProjectFilters"
                                aria-label="Filter"
                            >
                                <FilterListIcon
                                    fontSize="small"
                                    className={clsx(
                                        showFiltersRow &&
                                            classes.activeIconBackgroundColor,
                                        'view-filter',
                                        showFiltersRow && 'active'
                                    )}
                                />
                            </IconButton>
                        </GridItem>
                    </>
                )}
                {showTableSettings && !!tableSettings && (
                    <>
                        <GridItem className="table-settings-option-divider">
                            <Divider
                                orientation="vertical"
                                flexItem
                                className={classes.verticalDivider}
                            />
                        </GridItem>
                        <GridItem className="table-settings-option-container">
                            <TableSettings
                                saveTableViewSettings={saveTableViewSettings}
                                {...tableSettings}
                            />
                        </GridItem>
                    </>
                )}
            </GridItem>
        </GridContainer>
    )
}

export default SpecTableTitleSettings
